import { Copy, Trash2, Phone, Calendar, Star, Users, Clock, Check } from 'lucide-react';
import { useState } from 'react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { usePromoCode } from '@/hooks/usePromoCodes';
import type { PromoCode } from '@/types/promoCode.types';

const WHATSAPP_NUMBER = '447934514016';

function formatPhone(phone: string) {
  // Format international phone number
  if (phone.startsWith('55') && phone.length >= 12) {
    // Brazilian format: +55 11 99999-9999
    const country = phone.slice(0, 2);
    const area = phone.slice(2, 4);
    const part1 = phone.slice(4, 9);
    const part2 = phone.slice(9);
    return `+${country} ${area} ${part1}-${part2}`;
  }
  if (phone.startsWith('44') && phone.length >= 11) {
    // UK format: +44 7934 514016
    const country = phone.slice(0, 2);
    const part1 = phone.slice(2, 6);
    const part2 = phone.slice(6);
    return `+${country} ${part1} ${part2}`;
  }
  // Default: just add + prefix
  return `+${phone}`;
}

function formatDate(dateStr: string) {
  const date = new Date(dateStr);
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function formatDateTime(dateStr: string) {
  const date = new Date(dateStr);
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

interface PromoCodeDetailModalProps {
  codeId: string | null;
  onClose: () => void;
  onDeactivate: (code: PromoCode) => void;
}

export default function PromoCodeDetailModal({
  codeId,
  onClose,
  onDeactivate,
}: PromoCodeDetailModalProps) {
  const [copied, setCopied] = useState(false);
  const { data, isLoading, isError } = usePromoCode(codeId || '');

  const inviteLink = data?.code
    ? `https://wa.me/${WHATSAPP_NUMBER}?text=${data.code.code}`
    : '';

  const handleCopyLink = async () => {
    if (!inviteLink) return;
    try {
      await navigator.clipboard.writeText(inviteLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  if (!codeId) return null;

  return (
    <Modal
      isOpen={!!codeId}
      onClose={onClose}
      title={data?.code?.code || 'Carregando...'}
      size="lg"
    >
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Spinner size="lg" />
        </div>
      ) : isError || !data ? (
        <div className="text-center py-8">
          <p className="text-red-500 dark:text-red-400">
            Erro ao carregar detalhes do codigo
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Invite Link */}
          <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Link de Convite
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-sm bg-white dark:bg-gray-900 p-2 rounded border border-gray-200 dark:border-gray-700 break-all text-gray-800 dark:text-gray-200">
                {inviteLink}
              </code>
              <Button
                variant="primary"
                size="sm"
                onClick={handleCopyLink}
                leftIcon={copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              >
                {copied ? 'Copiado!' : 'Copiar'}
              </Button>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="text-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <div className="flex items-center justify-center gap-1 text-gray-500 dark:text-gray-400 mb-1">
                <Users className="w-4 h-4" />
              </div>
              <p className="text-lg font-bold text-gray-900 dark:text-white">
                {data.code.maxUses ? `${data.code.usedCount}/${data.code.maxUses}` : data.code.usedCount}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">usos</p>
            </div>
            <div className="text-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <div className="flex items-center justify-center gap-1 text-gray-500 dark:text-gray-400 mb-1">
                <Star className="w-4 h-4" />
              </div>
              <p className="text-lg font-bold text-gray-900 dark:text-white">
                {data.code.plan.charAt(0).toUpperCase() + data.code.plan.slice(1)}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">plano</p>
            </div>
            <div className="text-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <div className="flex items-center justify-center gap-1 text-gray-500 dark:text-gray-400 mb-1">
                <Clock className="w-4 h-4" />
              </div>
              <p className="text-lg font-bold text-gray-900 dark:text-white">
                {data.code.durationMonths === 0 ? 'Vitalicio' : `${data.code.durationMonths}m`}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">duracao</p>
            </div>
            <div className="text-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <div className="flex items-center justify-center gap-1 text-gray-500 dark:text-gray-400 mb-1">
                <Calendar className="w-4 h-4" />
              </div>
              <p className="text-lg font-bold text-gray-900 dark:text-white">
                {data.code.expiresAt ? formatDate(data.code.expiresAt) : '-'}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">expira</p>
            </div>
          </div>

          {/* Redemptions */}
          <div>
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Usuarios que ativaram ({data.redemptions.length})
            </h4>
            {data.redemptions.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                Nenhum usuario ativou este codigo ainda
              </p>
            ) : (
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg divide-y divide-gray-200 dark:divide-gray-700 max-h-64 overflow-y-auto">
                {data.redemptions.map((redemption) => (
                  <div key={redemption.id} className="p-3 flex items-center gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center">
                      <Phone className="w-4 h-4 text-gray-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {formatPhone(redemption.user.phoneNumber)}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {redemption.user.name || 'Sem nome'} &bull; Ativou em {formatDateTime(redemption.redeemedAt)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Actions */}
          {data.code.isActive && (
            <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
              <Button
                variant="danger"
                className="w-full"
                leftIcon={<Trash2 className="w-4 h-4" />}
                onClick={() => onDeactivate(data.code)}
              >
                Desativar Codigo
              </Button>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
